import { useState, useEffect, useCallback } from "react";
import { UserPlus, Trash2, Loader2, Crown, Mail, ShieldAlert, User as UserIcon, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { API, apiCall, formatDate } from "@/lib/api";

export const CompanyUsersTab = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ users: [], is_owner: false, company_name: "" });
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { ok, data: resp } = await apiCall("GET", `${API}/company/users`);
      if (ok) setData(resp);
      else toast.error(resp?.detail || "No se pudieron cargar los usuarios");
    } catch {
      toast.error("Error de red");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return toast.error("Ingresa un email");
    setAdding(true);
    try {
      const { ok, data: resp } = await apiCall("POST", `${API}/company/users`, { email });
      if (ok) {
        toast.success("Usuario agregado");
        setNewEmail("");
        await load();
      } else {
        toast.error(resp?.detail || "No se pudo agregar el usuario");
      }
    } catch {
      toast.error("Error de red");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      const { ok, data: resp } = await apiCall("DELETE", `${API}/company/users/${encodeURIComponent(confirmRemove.email)}`);
      if (ok) {
        toast.success("Usuario eliminado");
        setConfirmRemove(null);
        await load();
      } else {
        toast.error(resp?.detail || "No se pudo eliminar");
      }
    } catch {
      toast.error("Error de red");
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" style={{ color: "var(--app-text-muted)" }}>
        <Loader2 className="w-5 h-5 animate-spin mr-2" />Cargando usuarios...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-users-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: "var(--app-text)" }}>Usuarios de la empresa</h2>
          <p className="text-sm" style={{ color: "var(--app-text-secondary)" }}>
            Gestiona quien puede acceder a <span className="font-semibold">{data.company_name}</span>.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} style={{ color: "var(--app-text-secondary)" }} data-testid="refresh-users-btn">
          <RefreshCw className="w-4 h-4 mr-2" />Actualizar
        </Button>
      </div>

      {/* Owner notice for non-owners */}
      {!data.is_owner && (
        <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 flex items-start gap-3" data-testid="not-owner-notice">
          <ShieldAlert className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm" style={{ color: "var(--app-text-secondary)" }}>
            <p className="font-semibold text-amber-500">Solo el propietario puede invitar o eliminar usuarios.</p>
            <p className="mt-1">Podes ver la lista pero no modificarla. Contactá al propietario si necesitas cambios.</p>
          </div>
        </div>
      )}

      {/* Add form */}
      {data.is_owner && (
        <form onSubmit={handleAdd} className="p-5 rounded-lg" style={{ backgroundColor: "var(--app-card)", border: "1px solid var(--app-border)" }} data-testid="add-user-form">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus className="w-4 h-4 text-violet-500" />
            <h3 className="font-semibold text-sm" style={{ color: "var(--app-text)" }}>Invitar nuevo usuario</h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              placeholder="usuario@empresa.com"
              className="flex-1"
              disabled={adding}
              data-testid="new-user-email"
            />
            <Button type="submit" disabled={adding || !newEmail.trim()} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="add-user-btn">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-2" />Invitar</>}
            </Button>
          </div>
          <p className="text-xs mt-2" style={{ color: "var(--app-text-muted)" }}>
            El usuario invitado tendra que ingresar con Google usando exactamente ese email para acceder.
          </p>
        </form>
      )}

      {/* Users list */}
      <div className="rounded-lg overflow-hidden" style={{ backgroundColor: "var(--app-card)", border: "1px solid var(--app-border)" }}>
        <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--app-border)" }}>
          <h3 className="font-semibold text-sm" style={{ color: "var(--app-text)" }}>
            Usuarios activos ({data.users?.length || 0})
          </h3>
        </div>
        <div className="divide-y" style={{ borderColor: "var(--app-border)" }}>
          {(data.users || []).map((u) => (
            <div key={u.email} className="px-5 py-4 flex items-center justify-between gap-3" data-testid={`user-row-${u.email}`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {u.picture ? (
                  <img src={u.picture} alt={u.name || u.email} className="w-9 h-9 rounded-full" />
                ) : (
                  <div className="w-9 h-9 rounded-full flex items-center justify-center bg-violet-500/10">
                    <UserIcon className="w-4 h-4 text-violet-500" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm truncate" style={{ color: "var(--app-text)" }}>
                      {u.name || u.email}
                    </p>
                    {u.is_owner && (
                      <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 text-[10px] gap-1">
                        <Crown className="w-3 h-3" />Propietario
                      </Badge>
                    )}
                    {u.is_me && (
                      <Badge className="bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30 text-[10px]">
                        Vos
                      </Badge>
                    )}
                    {!u.name && (
                      <Badge className="bg-zinc-500/15 text-zinc-500 border-zinc-500/30 text-[10px]">
                        Pendiente 1er ingreso
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs truncate flex items-center gap-1 mt-0.5" style={{ color: "var(--app-text-muted)" }}>
                    <Mail className="w-3 h-3" />{u.email}
                    {u.joined_at && <span className="ml-2">· {formatDate(u.joined_at)}</span>}
                  </p>
                </div>
              </div>
              {data.is_owner && !u.is_owner && !u.is_me && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmRemove(u)}
                  className="text-rose-500 hover:text-rose-400 hover:bg-rose-500/10"
                  data-testid={`remove-user-${u.email}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          {(!data.users || data.users.length === 0) && (
            <div className="px-5 py-8 text-center text-sm" style={{ color: "var(--app-text-muted)" }}>
              No hay usuarios registrados.
            </div>
          )}
        </div>
      </div>

      {/* Info box */}
      <div className="p-4 rounded-lg text-xs" style={{ backgroundColor: "var(--app-card)", border: "1px solid var(--app-border)", color: "var(--app-text-muted)" }}>
        <p className="font-semibold mb-1" style={{ color: "var(--app-text-secondary)" }}>Como funciona</p>
        <ul className="space-y-1 list-disc pl-4">
          <li>Los usuarios invitados deben ingresar al sistema con <strong>Google</strong> usando exactamente el email invitado.</li>
          <li>Comparten el saldo, campañas y datos de la empresa.</li>
          <li>El propietario es el unico que puede invitar o eliminar usuarios.</li>
          <li>Al eliminar un usuario se cierra su sesion inmediatamente.</li>
        </ul>
      </div>

      {/* Confirm remove dialog */}
      <AlertDialog open={!!confirmRemove} onOpenChange={(o) => !o && setConfirmRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar usuario</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que queres eliminar a <strong>{confirmRemove?.email}</strong> de la empresa?
              Perdera acceso inmediatamente y sera desconectado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing} data-testid="cancel-remove">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} disabled={removing} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-remove">
              {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
